import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getServiceCategoryPillPresentation } from "../utils/constants";
import {
  SERVICE_CATEGORY_PILL_BASE_CLASS,
  resolveRfiBadgeStyle,
  rfiBadgeStyleToPillStyle,
} from "../utils/serviceCategoriesColors";
import { DEFAULT_RFI_BADGE_STYLE } from "../utils/rfiCatalogServiceNames";
import ServiceCategoryTooltip from "./ServiceCategoryTooltip";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Service category pill — colors from live RFI catalog (same source as Drawer / Assessment detail).
 * Falls back to legacy static palette when catalog is unavailable.
 */
export default function ServiceCategoryPill({ name, rfiId = null, className = "" }) {
  const trimmed = String(name || "").trim();
  const { rfis } = useSelector((state) => state.rfisColors);

  const presentation = useMemo(() => {
    if (!trimmed) return null;

    const catalogStyle =
      resolveRfiBadgeStyle({ rfi_id: rfiId, rfi_name: trimmed }, rfis) ??
      resolveRfiBadgeStyle(trimmed, rfis);

    if (catalogStyle) {
      const inlineStyle = rfiBadgeStyleToPillStyle(catalogStyle);
      if (inlineStyle) {
        return { className: SERVICE_CATEGORY_PILL_BASE_CLASS, style: inlineStyle };
      }
    }

    const legacy = getServiceCategoryPillPresentation(trimmed);
    const isBlueFallback =
      legacy.className?.includes("bg-blue-50") && Object.keys(legacy.style || {}).length === 0;

    if (isBlueFallback) {
      return {
        className: SERVICE_CATEGORY_PILL_BASE_CLASS,
        style: rfiBadgeStyleToPillStyle(DEFAULT_RFI_BADGE_STYLE),
      };
    }

    return legacy;
  }, [trimmed, rfiId, rfis]);

  if (!trimmed || !presentation) return null;

  const { className: pillClass, style } = presentation;

  return (
    <span style={style} className={classNames(pillClass, className)}>
      {trimmed}
      <ServiceCategoryTooltip name={trimmed} />
    </span>
  );
}
